// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldIcon } from "@guestsupply/MobiusOverrides/FormField/FormField";
import { searchInputStyles } from "@guestsupply/Overrides/Widgets/Header/SearchInput";
import CaretRight from "@guestsupply/XDL/Icons/CaretRight";
import { color, createLinkIconStyles, mergeWithCss, styledScrollbar, xScreen } from "@guestsupply/XDL/Utilities";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface AutoCompleteItems {
    headerText?: TypographyPresentationProps;
    itemText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    focusedItemText?: TypographyPresentationProps;
    focusedLink?: LinkPresentationProps;
}

const sharedAutoCompleteTextStyles: TypographyPresentationProps = {
    size: 12,
    weight: "normal",
    color: "text.main",
    css: css`
        letter-spacing: 0;
        line-height: 15px;
    `,
};

export const autoCompleteSharedStyles: AutoCompleteItems = {
    headerText: {
        size: 16,
        weight: "bold",
        color: "text",
        css: css`
            letter-spacing: 0;
            line-height: 19px;
            margin: 0 0 12px 0;
            &:not(:first-child) {
                margin: 12px 0 7px 0;
            }
        `,
    },
    itemText: {
        ...mergeWithCss(sharedAutoCompleteTextStyles, {
            css: css`
                margin-bottom: 8px;
            `,
        }),
    },
    link: {
        color: "text.main",
        typographyProps: {
            ...sharedAutoCompleteTextStyles,
        },
    },
    focusedItemText: {
        ...mergeWithCss(sharedAutoCompleteTextStyles, {
            css: css`
                margin-bottom: 8px;
            `,
        }),
    },
    focusedLink: {
        typographyProps: {
            ...sharedAutoCompleteTextStyles,
        },
        css: css`
            background: ${color("common.ui3")};
        `,
    },
};

searchInputStyles.input = {
    sizeVariant: "large" as any,
    cssOverrides: {
        inputSelect: css`
            box-shadow: inset 0 1px 0 0 ${color("common.ui7", 0.1)};
            ${xScreen("lte", "md")} {
                height: 36px;
            }
        `,
        formInputWrapper: css`
            &&&& input {
                padding: 0 35px 0 16px;
            }
        `,
    },
};

searchInputStyles.popover = {
    transitionDuration: "regular",
    toggle: false,
};

searchInputStyles.popoverWrapper = {
    as: "div",
    _height: "auto",
    css: css`
        display: inline-flex;
        justify-content: flex-end;
    `,
};

searchInputStyles.popoverContentBody = {
    as: "div",
    _height: "100%",
};
searchInputStyles.autocompleteContainer = {
    css: css`
        overflow: auto;
        ${styledScrollbar(4)}
        max-height: calc(100vh - 250px);
    `,
};
searchInputStyles.autocompleteWrapper = {
    css: css`
        display: flex;
        flex-direction: row;
        ${xScreen("lte", "sm")} {
            flex-direction: column;
        }
        ${({ hasContent, width }: any) => (hasContent ? `min-width: ${width}px;` : `width: ${width}px;`)}
    `,
};

searchInputStyles.autocompleteColumnWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        width: 272px;
        padding: 16px;
        border: 1px solid ${color("common.border")};
        background-color: ${color("common.ui2")};
        border-bottom: none;
        border-top: none;
        ${xScreen("lte", "sm")} {
            width: 100%;
            background-color: ${color("common.ui1")};
            padding-bottom: 24px;
            border: none;
        }
    `,
};

searchInputStyles.autocompleteCommerceSearchWrapper = {
    css: css`
            display: flex;
            flex-direction: column;
            padding: 0 20px 15px 20px;
        `,
};

searchInputStyles.autocompleteCommerceSearchWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        padding: 0 20px 15px 20px;
    `,
};

searchInputStyles.autocompleteColumnProductWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        ${({ hasContent }: any) => (hasContent ? "width: 427px" : "width: 100%")};
        padding: 16px;
        ${xScreen("lte", "md")} {
            width: auto;
        }
        ${xScreen("lte", "sm")} {
            width: auto;
            padding-top: 0;
        }
    `,
};

searchInputStyles.tippyPopup = {
    main: {
        css: css`
            &.tippy-box {
                padding-right: 2px; //more space for scrollbar
                & > .tippy-content {
                    overflow: hidden;
                    max-height: calc(100vh - 250px);
                }
            }
        `,
    },
};

searchInputStyles.autocompleteCategoriesStyles = {};
searchInputStyles.autocompleteBrandsStyles = {
    headerText: {
        ...autoCompleteSharedStyles?.headerText,
    },
    link: {
        ...autoCompleteSharedStyles?.link,
    },
    focusedBrandItemText: {
        ...autoCompleteSharedStyles?.focusedItemText,
    },
    focusedLink: {
        ...autoCompleteSharedStyles?.focusedLink,
    },
    brandItemText: {
        ...autoCompleteSharedStyles?.itemText,
    },
};
searchInputStyles.autocompleteContentStyles = {
    headerText: {
        ...autoCompleteSharedStyles?.headerText,
    },
    link: {
        ...autoCompleteSharedStyles?.link,
    },
    focusedLink: {
        ...autoCompleteSharedStyles?.focusedLink,
    },
};
searchInputStyles.autocompleteColumnProductMobileDivider = {
    css: css`
        display: none;
        ${xScreen("lte", "sm")} {
            display: block;
            border-top: 1px solid ${getColor("common.border")};
            padding-top: 24px;
        }
    `,
};

searchInputStyles.viewAllLink = {
    ...createLinkIconStyles(CaretRight, {
        typographyProps: {
            size: 12,
        },
        icon: {
            iconProps: {
                size: 10,
            },
        },
        css: css`
            margin-bottom: 12px;
        `,
    }),
};
